/**
 * The editing commands of a timeline selection (1.7.3): Delete (lift), Shift+Delete (ripple, with Sync), cut, copy
 * and paste at the playhead, link and unlink, relink a detached sound, split, mute and reveal the source. One list for
 * the keys (Editor), the context menu (Timeline) and the Inspector's multi-selection summary. The edits are the
 * contracts' ops (docs/dev/editor-1.7.3-build.md); the pure parts are exported for the unit tests.
 */
import {
  CROSSFADE_ALIGN_LABELS, LINKABLE_KINDS, TICKS_PER_SECOND, allAudioTracks, appliedCrossfade, bestCrossfadeAlign, crossfadeAlignOf, crossfadeLimits, itemSpan, layoutVideo, ops, relinkProblem,
  type CrossfadeAlign, type ItemRef, type RippleMode, type SequenceDocument, type SourceDurations, type SplitScope,
} from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { toast } from '../../components/Toasts';
import { readClipboard, writeClipboard } from './clipboard';
import { NO_SELECTION, type TimelineSelection } from './selection';

export type CommandId = 'cut' | 'copy' | 'paste' | 'paste-insert' | 'delete' | 'ripple-delete' | 'link' | 'unlink' | 'relink' | 'split' | 'mute' | 'reveal' | 'crossfade'
  | 'xfade-center' | 'xfade-start' | 'xfade-end' | 'sound-to-track';

/** 1.8.0: the alignment commands of a crossfade (the context menu's radio items), by alignment. */
export const XFADE_ALIGN_COMMANDS: Record<CrossfadeAlign, CommandId> = { center: 'xfade-center', start: 'xfade-start', end: 'xfade-end' };

const CLIP_KINDS = new Set(['video', 'audio', 'layer']);
const isLinkable = (r: ItemRef) => (LINKABLE_KINDS as readonly string[]).includes(r.kind);
const rippleOf = (sync: boolean): RippleMode => (sync ? 'sync' : 'v1');

/** Every clip on any track by id (V1, layers, A2–A8). */
function clipById(doc: SequenceDocument, id: string) {
  return doc.video.find((c) => c.id === id)
    ?? (doc.layers ?? []).flatMap((l) => l.clips).find((c) => c.id === id)
    ?? allAudioTracks(doc).flatMap((t) => t.clips).find((c) => c.id === id);
}

/**
 * Delete (lift) or Shift+Delete (ripple) the items. A selection of gaps only closes them: closeGap, or closeGapSync
 * for a ripple with Sync on (the later gaps first, so the earlier ones stay where they are).
 */
export function deleteEdit(doc: SequenceDocument, items: readonly ItemRef[], ripple: boolean, sync: boolean): SequenceDocument {
  if (!items.length) return doc;
  if (items.every((r) => r.kind === 'gap')) {
    const order = new Map(doc.video.map((c, i) => [c.id, i]));
    const gaps = [...items].sort((a, b) => (order.get(b.id) ?? 0) - (order.get(a.id) ?? 0));
    return gaps.reduce((d, g) => (ripple && sync ? ops.closeGapSync(d, g.id) : ops.closeGap(d, g.id)), doc);
  }
  return ripple ? ops.rippleDelete(doc, items, rippleOf(sync)) : ops.liftClips(doc, items);
}

/** The clips among the items (V1, layers, audio tracks). */
export const clipItems = (items: readonly ItemRef[]): ItemRef[] => items.filter((r) => CLIP_KINDS.has(r.kind));

/** Whether every selected clip is muted (Mute then unmutes them). */
export function allMuted(doc: SequenceDocument, items: readonly ItemRef[]): boolean {
  const clips = clipItems(items).map((r) => clipById(doc, r.id)).filter(Boolean);
  return clips.length > 0 && clips.every((c) => c!.muted);
}

/** Mute the selected clips, or unmute them when all of them are muted. */
export function muteEdit(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
  const muted = !allMuted(doc, items);
  return clipItems(items).reduce((d, r) => ops.updateClip(d, r.id, { muted }), doc);
}

/** What Link and Unlink can do with these items. */
export function linkState(doc: SequenceDocument, items: readonly ItemRef[]): { canLink: boolean; canUnlink: boolean } {
  const linkable = items.filter(isLinkable);
  const linkIdOf = (r: ItemRef) => (r.kind === 'graphic' ? doc.graphics.find((g) => g.id === r.id)?.linkId : (clipById(doc, r.id) as { linkId?: string } | undefined)?.linkId);
  const ids = linkable.map(linkIdOf);
  const linked = ids.some(Boolean);
  // Items already in one link group together (a clicked group) have nothing to link.
  const oneGroup = Boolean(ids[0]) && ids.every((x) => x === ids[0]);
  const attached = linkable.some((r) => r.kind === 'video' && doc.video.some((c) => c.id === r.id && !c.soundDetached));
  return { canLink: linkable.length >= 2 && !oneGroup, canUnlink: linked || attached };
}

/**
 * The detached sound to put back into a V1 clip (Relink): the selected one (or, with only the picture selected, any
 * audio clip of the same source, the lined-up one first), and why it cannot be linked (relinkProblem), or null when
 * the selection has no detached V1 clip.
 */
export function relinkTarget(doc: SequenceDocument, items: readonly ItemRef[]): { clipId: string; audioId: string | null; problem: string | null } | null {
  const pictures = items.filter((r) => r.kind === 'video' && doc.video.some((c) => c.id === r.id && c.soundDetached));
  if (pictures.length !== 1) return null;
  const clip = doc.video.find((c) => c.id === pictures[0]!.id)!;
  const chosen = items.filter((r) => r.kind === 'audio').map((r) => r.id);
  const sameSource = allAudioTracks(doc).flatMap((t) => t.clips).filter((a) => a.versionId === clip.versionId).map((a) => a.id);
  const candidates = chosen.length ? chosen : sameSource;
  if (!candidates.length) return { clipId: clip.id, audioId: null, problem: 'Pick the sound on an audio track' };
  const fit = candidates.find((a) => relinkProblem(doc, clip.id, a) === null);
  if (fit) return { clipId: clip.id, audioId: fit, problem: null };
  return { clipId: clip.id, audioId: candidates[0]!, problem: relinkProblem(doc, clip.id, candidates[0]!) };
}

/** The asset the primary item (a clip) comes from, for Reveal source in bin. */
export function sourceAssetOf(doc: SequenceDocument, ref: ItemRef | null): string | null {
  if (!ref || !CLIP_KINDS.has(ref.kind)) return null;
  return clipById(doc, ref.id)?.assetId ?? null;
}

/** Whether the playhead is over a V1 clip that Split at playhead would cut. */
export function canSplitAt(doc: SequenceDocument, playhead: number): boolean {
  return layoutVideo(doc).some((p) => playhead > p.recordIn && playhead < p.recordOut);
}

/**
 * What S (and Split at playhead) splits (1.7.4): the selected clips and graphics under the playhead, or V1 with
 * nothing selected (or none of the selection under it).
 */
export function splitScope(doc: SequenceDocument, items: readonly ItemRef[], playhead: number): SplitScope {
  const under = items.filter((r) => r.kind !== 'gap' && r.kind !== 'cue' && r.kind !== 'marker').filter((r) => {
    const s = itemSpan(doc, r);
    return Boolean(s && playhead > s.start && playhead < s.end);
  });
  return under.length ? { items: under } : 'v1';
}

/** Whether S would cut something: a selected clip or graphic under the playhead, or a V1 clip there. */
export function canSplitSelection(doc: SequenceDocument, items: readonly ItemRef[], playhead: number): boolean {
  return splitScope(doc, items, playhead) !== 'v1' || canSplitAt(doc, playhead);
}

/**
 * The clip a crossfade goes into (the primary item when it is a V1 or audio clip), and whether it has one already.
 * 1.8.0: with the source lengths (`lengths`), a new crossfade is possible when any alignment fits, `align` is the one it
 * has (or a new one would get); a clip whose source is silent (`silent`, by version id) has the problem "This clip has
 * no sound".
 */
export function crossfadeTarget(doc: SequenceDocument, sel: { primary: ItemRef | null; items: readonly ItemRef[] }, lengths?: SourceDurations, silent?: (versionId: string) => boolean):
  { ref: ItemRef; problem: string | null; has: boolean; align: CrossfadeAlign; ticks: number } | null {
  const ref = sel.primary ?? sel.items[0] ?? null;
  if (!ref || (ref.kind !== 'video' && ref.kind !== 'audio')) return null;
  const clip = clipById(doc, ref.id) as { versionId: string; crossfadeAlign?: 'center' | 'start' } | undefined;
  if (clip && silent?.(clip.versionId)) return { ref, problem: 'This clip has no sound', has: false, align: 'end', ticks: 0 };
  const ticks = appliedCrossfade(doc, ref, lengths);
  if (ticks > 0) return { ref, problem: null, has: true, align: clip ? crossfadeAlignOf(clip) : 'end', ticks };
  const best = lengths ? bestCrossfadeAlign(doc, ref, CROSSFADE_TICKS, lengths) : null;
  return { ref, problem: best ? null : crossfadeLimits(doc, ref, { align: lengths ? 'center' : 'end', durations: lengths }).problem, has: false, align: best ?? 'end', ticks: 0 };
}

/** The toast of a crossfade just set (1.8.0): its length and where it sits, and why when it is not centred. */
export function crossfadeToast(doc: SequenceDocument, ref: ItemRef, lengths?: SourceDurations): string {
  const clip = clipById(doc, ref.id) as { crossfadeAlign?: 'center' | 'start' } | undefined;
  const align = clip ? crossfadeAlignOf(clip) : 'end';
  const text = `Crossfade of ${(appliedCrossfade(doc, ref, lengths) / TICKS_PER_SECOND).toFixed(2)} s · ${CROSSFADE_ALIGN_LABELS[align].replace('Centre', 'Centred')}`;
  if (!lengths || align === 'center') return text;
  const why = crossfadeLimits(doc, ref, { align: 'center', durations: lengths }).problem?.replace(/ to crossfade with$/, '');
  return `${text}: ${why ? `${why.charAt(0).toLowerCase()}${why.slice(1)}` : 'there is not enough media to centre it'}`;
}

/** The video-track clip whose own sound "Move sound to an audio track" moves (1.8.0): the primary layer clip, not muted, not silent. */
export function soundToTrackTarget(doc: SequenceDocument, sel: { primary: ItemRef | null }, silent?: (versionId: string) => boolean): string | null {
  const ref = sel.primary;
  if (ref?.kind !== 'layer') return null;
  const c = (doc.layers ?? []).flatMap((l) => l.clips).find((x) => x.id === ref.id);
  return c && !c.muted && !silent?.(c.versionId) ? c.id : null;
}

/** The length a new crossfade gets (1 s, clamped by the handles: ops.setCrossfade). */
export const CROSSFADE_TICKS = TICKS_PER_SECOND;

/* ---------------------------------------------------------------- running the commands */

export interface CommandEnv {
  doc: SequenceDocument;
  apply: (next: SequenceDocument | ((d: SequenceDocument) => SequenceDocument), coalesce?: string) => void;
  seal?: () => void;
  /** The playhead now (paste and split happen there). */
  playhead: () => number;
  /** The Sync toggle (usePref editor.syncRipple). */
  sync: boolean;
  canEdit: boolean;
  setSelection: (s: TimelineSelection) => void;
  /** Reveal a source asset in the bin and the source viewer. */
  reveal?: (assetId: string) => void;
  /** 1.8.0: the source lengths (crossfades centred on the cut), and which sources are silent, by version id. */
  lengths?: SourceDurations;
  silent?: (versionId: string) => boolean;
}

/** One entry of the context menu or a separator. */
export type MenuEntry = { id: CommandId; label: string; keys?: string; disabled?: boolean; danger?: boolean; checked?: boolean } | 'separator';

/** The context menu of a selection (1.7.3): Cut, Copy, Paste, Delete, Ripple delete, Unlink/Link, Split, Mute, Reveal. */
export function commandMenu(env: Pick<CommandEnv, 'doc' | 'canEdit' | 'playhead' | 'sync' | 'lengths' | 'silent'>, sel: TimelineSelection, clipboard = readClipboard() !== null): MenuEntry[] {
  const { doc, canEdit } = env;
  const items = sel.items;
  const any = items.length > 0;
  const real = items.some((r) => r.kind !== 'gap');
  const links = linkState(doc, items);
  const relink = relinkTarget(doc, items);
  const clips = clipItems(items).length > 0;
  const out: MenuEntry[] = [
    { id: 'cut', label: 'Cut', keys: '⌘X', disabled: !canEdit || !real },
    { id: 'copy', label: 'Copy', keys: '⌘C', disabled: !real },
    { id: 'paste', label: 'Paste at the playhead', keys: '⌘V', disabled: !canEdit || !clipboard },
    { id: 'paste-insert', label: 'Paste and insert', keys: '⇧⌘V', disabled: !canEdit || !clipboard },
    'separator',
    { id: 'delete', label: items.length && items.every((r) => r.kind === 'gap') ? 'Close gap' : 'Delete (leave a gap)', keys: '⌫', disabled: !canEdit || !any, danger: true },
    { id: 'ripple-delete', label: env.sync ? 'Ripple delete (Sync)' : 'Ripple delete', keys: '⇧⌫', disabled: !canEdit || !any, danger: true },
    'separator',
  ];
  if (relink) out.push({ id: 'relink', label: 'Relink sound', disabled: !canEdit });
  if (links.canUnlink || !links.canLink) out.push({ id: 'unlink', label: 'Unlink', keys: '⇧⌘L', disabled: !canEdit || !links.canUnlink });
  if (links.canLink) out.push({ id: 'link', label: 'Link', keys: '⌘L', disabled: !canEdit });
  out.push(
    { id: 'split', label: 'Split at playhead', keys: 'S', disabled: !canEdit || !canSplitSelection(doc, items, env.playhead()) },
    { id: 'mute', label: allMuted(doc, items) ? 'Unmute' : 'Mute', disabled: !canEdit || !clips },
  );
  const xfade = crossfadeTarget(doc, sel, env.lengths, env.silent);
  // 1.8.0: a silent clip has no crossfade; one with a crossfade offers where it sits (radio items, the current one marked).
  if (xfade && xfade.problem !== 'This clip has no sound') {
    if (xfade.has) {
      for (const a of ['center', 'start', 'end'] as const) {
        const fits = !crossfadeLimits(doc, xfade.ref, { align: a, durations: env.lengths }).problem;
        // The menu has no radio items of its own: the choice is marked in the label (● chosen, ○ not).
        out.push({ id: XFADE_ALIGN_COMMANDS[a], label: `${xfade.align === a ? '●' : '○'} ${CROSSFADE_ALIGN_LABELS[a]}`, checked: xfade.align === a, disabled: !canEdit || !fits });
      }
    }
    out.push({ id: 'crossfade', label: xfade.has ? 'Remove crossfade' : 'Crossfade', keys: '⇧⌘D', disabled: !canEdit });
  }
  if (soundToTrackTarget(doc, sel, env.silent)) out.push({ id: 'sound-to-track', label: 'Move sound to an audio track', disabled: !canEdit });
  out.push(
    'separator',
    { id: 'reveal', label: 'Reveal source in bin', disabled: !sourceAssetOf(doc, sel.primary ?? items[0] ?? null) },
  );
  return out;
}

/** Run a command on a selection. Edits that fail (an op refusing) are an error toast, never a crash. */
export function runCommand(env: CommandEnv, id: CommandId, sel: TimelineSelection, opts: { only?: boolean } = {}): void {
  const { doc, apply, seal } = env;
  const items = sel.items;
  const edit = (next: SequenceDocument) => { seal?.(); apply(next); seal?.(); };
  try {
    switch (id) {
      case 'copy':
      case 'cut': {
        const payload = ops.copyItems(doc, items);
        if (!payload) { toast('Select clips, graphics, subtitles or markers to copy', 'error'); return; }
        writeClipboard(payload);
        if (id === 'cut' && env.canEdit) { edit(ops.liftClips(doc, items)); env.setSelection(NO_SELECTION); }
        toast(`${id === 'cut' ? 'Cut' : 'Copied'} ${payload.items.length} item${payload.items.length === 1 ? '' : 's'}`);
        return;
      }
      case 'paste':
      case 'paste-insert': {
        if (!env.canEdit) return;
        const payload = readClipboard();
        if (!payload) { toast('Nothing to paste: copy clips first (⌘C)', 'error'); return; }
        const r = ops.pasteItems(doc, payload, env.playhead(), id === 'paste' ? 'overwrite' : 'insert', { ripple: rippleOf(env.sync) });
        if (r.doc === doc) { toast('There is no room to paste here', 'error'); return; }
        edit(r.doc);
        env.setSelection({ items: r.pasted, primary: r.pasted[0] ?? null });
        return;
      }
      case 'delete':
      case 'ripple-delete': {
        if (!env.canEdit || !items.length) return;
        edit(deleteEdit(doc, items, id === 'ripple-delete', env.sync));
        env.setSelection(NO_SELECTION);
        return;
      }
      case 'link': {
        if (!env.canEdit) return;
        const next = ops.link(doc, items);
        if (next === doc) { toast('Select two or more clips or graphics to link', 'error'); return; }
        edit(next);
        return;
      }
      case 'unlink': {
        if (!env.canEdit) return;
        const next = ops.unlinkGroup(doc, items);
        if (next === doc) return;
        edit(next);
        // The unlinked parts are selected one by one from now on: keep only the item clicked.
        if (sel.primary && !opts.only) env.setSelection({ items: [sel.primary], primary: sel.primary });
        return;
      }
      case 'relink': {
        if (!env.canEdit) return;
        const t = relinkTarget(doc, items);
        if (!t) { toast('Select a clip whose sound is unlinked', 'error'); return; }
        if (t.problem || !t.audioId) { toast(t.problem ?? 'Pick the sound on an audio track', 'error'); return; }
        edit(ops.relink(doc, t.clipId, t.audioId));
        env.setSelection({ items: [{ kind: 'video', id: t.clipId }], primary: { kind: 'video', id: t.clipId } });
        return;
      }
      case 'split': {
        if (env.canEdit) edit(ops.splitAt(doc, env.playhead(), splitScope(doc, items, env.playhead())));
        return;
      }
      case 'crossfade': {
        if (!env.canEdit) return;
        const t = crossfadeTarget(doc, sel, env.lengths, env.silent);
        if (!t) { toast('Pick a clip on V1 or an audio track', 'error'); return; }
        if (t.has) { edit(ops.setCrossfade(doc, t.ref, 0)); toast('Removed the crossfade'); return; }
        if (t.problem) { toast(t.problem, 'error'); return; }
        // 1.8.0: centred where the media allows (ops.setCrossfade with the lengths), else the alignment that fits.
        const next = ops.setCrossfade(doc, t.ref, CROSSFADE_TICKS, undefined, env.lengths);
        if (next === doc) return;
        edit(next);
        toast(crossfadeToast(next, t.ref, env.lengths));
        return;
      }
      case 'xfade-center':
      case 'xfade-start':
      case 'xfade-end': {
        if (!env.canEdit) return;
        const t = crossfadeTarget(doc, sel, env.lengths, env.silent);
        if (!t?.has) return;
        const align = (Object.keys(XFADE_ALIGN_COMMANDS) as CrossfadeAlign[]).find((a) => XFADE_ALIGN_COMMANDS[a] === id)!;
        if (align === t.align) return;
        const next = ops.setCrossfade(doc, t.ref, t.ticks, align, env.lengths);
        if (next === doc) { toast(crossfadeLimits(doc, t.ref, { align, durations: env.lengths }).problem ?? 'The crossfade cannot sit there', 'error'); return; }
        edit(next);
        toast(crossfadeToast(next, t.ref, env.lengths));
        return;
      }
      case 'sound-to-track': {
        if (!env.canEdit) return;
        const id2 = soundToTrackTarget(doc, sel, env.silent);
        if (!id2) return;
        const r = ops.moveSoundToTrack(doc, id2);
        if (r.doc === doc) { toast(r.notes[0] ?? 'The sound stays in the clip', 'error'); return; }
        edit(r.doc);
        toast('Moved the sound to an audio track, linked to the clip');
        return;
      }
      case 'mute': {
        if (env.canEdit) edit(muteEdit(doc, items));
        return;
      }
      case 'reveal': {
        const asset = sourceAssetOf(doc, sel.primary ?? items[0] ?? null);
        if (asset) env.reveal?.(asset);
        return;
      }
    }
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}
